import { FinalData } from "@/types/finaldata";
import { Frame } from "@/types/frame";
import { Section } from "@/types/section";
import axios from "axios";
import JSZip from "jszip";

class SectionsService { 
    public getFrames(sections: Section[]): Frame[] {
        const frames: Frame[] = [];
        for (const section of sections) {
            for (const frame of section.frames) {
                frames.push(frame);
            }
            for (const subsection of section.subsections) {
                frames.push(...this.getFrames([subsection]));
            }
        }
        return frames;
    }
    public getFrameByNumber(sections: Section[], frameNumber: number): Frame | null {
        for (const section of sections) {
            for (const frame of section.frames) {
                if (frame.number === frameNumber) {
                    return frame;
                }
            }
            for (const subsection of section.subsections) {
                const foundFrame = this.getFrameByNumber([subsection], frameNumber);
                if (foundFrame) {
                    return foundFrame;
                }
            }
        }
        return null;
    }

    public updateFrame(sections: Section[], frame: Frame): Frame {
        for (const section of sections) {
            for (let i = 0; i < section.frames.length; i++) {
                if (section.frames[i].number === frame.number) {
                    section.frames[i] = frame;
                    return frame;
                }
            }
            for (const subsection of section.subsections) {
                const foundFrame = this.updateFrame([subsection], frame);
                if (foundFrame) {
                    return foundFrame;
                }
            }
        }
        return frame;
    }

    public setTemplateInFrameToNull(sections: Section[]): Section[] {
        for (const section of sections) {
            for (const frame of section.frames) {
                frame.template = undefined;
            }
            for (const subsection of section.subsections) {
                this.setTemplateInFrameToNull([subsection]);
            }
        }
        return sections;
    }

    public removeFramesWithIsReplacedFalse(sections: Section[]): Section[] {
        for (const section of sections) {
            for (let i = section.frames.length - 1; i >= 0; i--) {
                const frame = section.frames[i];
                if (frame.isReplaced === false || frame.isReplaced === undefined  || frame.selectedSlideTemplate === undefined) {
                    section.frames.splice(i, 1);
                }
            }
            for (const subsection of section.subsections) {
                this.removeFramesWithIsReplacedFalse([subsection]);
            }
        }
        return sections;
    }

    // remove frames where frames is not replaced
    public removeFrames(sections: Section[], frames: Frame[]): Section[] {
        for (const section of sections) {
            for (let i = section.frames.length - 1; i >= 0; i--) {
                const frame = section.frames[i];
                const foundFrame = frames.find(f => f.number === frame.number);
                if (foundFrame) {
                    section.frames.splice(i, 1);
                }
            }
            for (const subsection of section.subsections) {
                this.removeFrames([subsection], frames);
            }
        }
        return sections;

    }
    // function to send file and sections to backend
    public async generateLatex(latexCodeForSlide: File | null, sections: Section[]): Promise< FinalData | null> {
        if(latexCodeForSlide ===  null || sections.length === 0){
            return null;
        }

        sections = this.setTemplateInFrameToNull(sections);

        const formData = new FormData();
        formData.append('file', latexCodeForSlide);
        formData.append('sections', JSON.stringify(sections));

        try {
            const response = await axios.post( process.env.VUE_APP_BACKEND_FLASK + 'latizes/generate/zip', formData, {
                responseType: 'blob',
            });
            const zipblob = new Blob([response.data], { type: 'application/zip' });
            const zip = await JSZip.loadAsync(zipblob);
            let pdfBlob = null;

            for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
                if (zipEntry.name.endsWith('/presentation.pdf') || zipEntry.name === 'presentation.pdf') {
                    pdfBlob = await zipEntry.async('blob');
                    break; 
                }
            }

            if (!pdfBlob) {
                console.log('PDF file not found in zip');
                return null;
            }

            const pdfUrl = URL.createObjectURL(pdfBlob);
            const finalFile = new File([zipblob], 'presentation.zip', { type: 'application/zip' });

            return {
                finalPdfSource: pdfUrl,
                finalFile: finalFile}
        } catch (error : any) {
            if(axios.isAxiosError(error)) {
                console.error('Error message:', error.message);
            } else {
                console.error('An unknown error occurred');
            }
            return null;
        }
    }
    
    public async convertToPDF(latexCodeForSlide: File | null): Promise<{ sections: Section[]; pdfSource: string } | null> {
        if(latexCodeForSlide ===  null){
            return null;
        }

        const formData = new FormData();
        formData.append('file', latexCodeForSlide);

        try {
            const response = await axios.post( process.env.VUE_APP_BACKEND_FLASK + 'latizes/upload/zip', formData, {
                responseType: 'blob',
            });

            const zipResult = await JSZip.loadAsync(response.data);
            const pdfBlob = await zipResult.file('presentation.pdf')!.async('blob');
            const sectionsText = await zipResult.file('sections.json')!.async('text');
            const sections = JSON.parse(sectionsText) as Section[];
            const data = {
                sections: sections,
                pdfSource: URL.createObjectURL(pdfBlob)
            }
            return data;
        } catch (error : any) {
            if(axios.isAxiosError(error)) {
                console.error('Error message:', error.message);
            } else {
                console.error('An unknown error occurred');
            }
            return null;
        }
    }

    public getAllFrames(sections: Section[]): Frame[] {
        const frames: Frame[] = [];
        for (const section of sections) {
            frames.push(...section.frames);
            for (const subsection of section.subsections) {
                frames.push(...this.getAllFrames([subsection]));
            }
        }
        frames.sort((a, b) => a.number - b.number);
        return frames;
    }
 
}
export const sectionsService = new SectionsService();
